import { ArrowCircleUp as ArrowUpCircle, DirectionsWalk as Footprints, CropSquare as Frame, Terrain as Mountain, Palette, AutoAwesome as Sparkles, WbSunny as Sun, Park as Trees, AutoFixHigh as Wand2 } from "@mui/icons-material";
import { Box, Button, Divider, Slider, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { sliderValue } from '../canvas/slider-utils';


import type { LandTool } from "../canvas/types";

interface BrushPanelProps {
    activeLandTool: LandTool;
    setActiveLandTool: (tool: LandTool) => void;
    brushSize: number;
    setBrushSize: (size: number) => void;
    roughness: number;
    setRoughness: (value: number) => void;
    paintOpacity: number;
    setPaintOpacity: (value: number) => void;
    brush: string;
    setBrush: (brush: string) => void;
    stampType?: 'mountain' | 'forest' | 'dune' | 'swamp';
    setStampType?: (type: 'mountain' | 'forest' | 'dune' | 'swamp') => void;
    stampDensity?: number;
    setStampDensity?: (density: number) => void;
}

const terrainBrushes = [
    { name: 'land', icon: Frame, label: 'Parchment' },
    { name: 'desert', icon: Sun, label: 'Desert' },
    { name: 'forest', icon: Trees, label: 'Forest' },
    { name: 'mountain', icon: Mountain, label: 'Mountain' },
    { name: 'farm land', icon: Footprints, label: 'Farm Land' },
];

export function BrushPanel({
    activeLandTool,
    setActiveLandTool,
    brushSize,
    setBrushSize,
    roughness,
    setRoughness,
    paintOpacity,
    setPaintOpacity,
    brush,
    setBrush
}: BrushPanelProps) {
    const { t } = useTranslation();
    return (
        <Stack spacing={3}>
            <ToggleButtonGroup
                value={activeLandTool === 'raise' || activeLandTool === 'sink' ? 'sculpt' : activeLandTool}
                exclusive
                onChange={(_, newTool) => {
                    if (newTool === 'sculpt') {
                        setActiveLandTool('raise');
                    } else if (newTool === 'magic' || newTool === 'paint' || newTool === 'stamp') {
                        setActiveLandTool(newTool);
                    }
                }}
                aria-label="land tool"
                size="small"
                fullWidth
            >
                <ToggleButton value="magic" aria-label="magic">
                    <Tooltip title={t('map_creator.brush_panel.magic_tooltip')}><Wand2 sx={{ fontSize: '18px' }} /></Tooltip>
                </ToggleButton>
                <ToggleButton value="sculpt" aria-label="sculpt">
                    <Tooltip title={t('map_creator.brush_panel.sculpt_tooltip')}><ArrowUpCircle sx={{ fontSize: '18px' }} /></Tooltip>
                </ToggleButton>
                <ToggleButton value="paint" aria-label="paint">
                    <Tooltip title={t('map_creator.brush_panel.paint_tooltip')}><Palette sx={{ fontSize: '18px' }} /></Tooltip>
                </ToggleButton>
                <ToggleButton value="stamp" aria-label="stamp">
                    <Tooltip title={t('map_creator.brush_panel.stamp_tooltip')}><Sparkles sx={{ fontSize: '18px' }} /></Tooltip>
                </ToggleButton>
            </ToggleButtonGroup>

            <Divider />

            <Box>
                {activeLandTool === 'magic' && (
                    <Box sx={{ p: 2, border: '1px dashed grey', borderRadius: 1, bgcolor: 'action.hover' }}>
                        <Typography variant="body2" color="text.secondary" fontStyle="italic" gutterBottom>
                            {t('map_creator.brush_panel.magic_placeholders')}
                        </Typography>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption">{t('map_creator.brush_panel.pattern')}</Typography>
                                <Slider disabled min={0} max={10} defaultValue={5} size="small" />
                            </Box>
                            <Box>
                                <Typography variant="caption">{t('map_creator.brush_panel.detail')}</Typography>
                                <Slider disabled min={0} max={10} defaultValue={5} size="small" />
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Button disabled variant="contained" size="small" fullWidth>{t('map_creator.brush_panel.generate')}</Button>
                                <Button disabled variant="outlined" size="small" fullWidth>{t('map_creator.brush_panel.invert')}</Button>
                            </Stack>
                        </Stack>
                    </Box>
                )}

                {(activeLandTool === 'raise' || activeLandTool === 'sink' || activeLandTool === 'paint') && (
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="caption" gutterBottom>{t('map_creator.brush_panel.brush_size', { size: brushSize })}</Typography>
                            <Slider
                                min={1}
                                max={10}
                                step={1}
                                value={brushSize}
                                onChange={(_, v) => setBrushSize(sliderValue(v))}
                                size="small"
                            />
                        </Box>

                        {(activeLandTool === 'raise' || activeLandTool === 'sink') && (
                            <Stack spacing={2.5}>
                                <Box>
                                    <Typography variant="caption" display="block" mb={1} fontWeight="bold" sx={{ textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.65rem' }}>
                                        {t('map_creator.brush_panel.sculpt_mode')}
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={activeLandTool}
                                        exclusive
                                        onChange={(_, newMode) => {
                                            if (newMode === 'raise' || newMode === 'sink') {
                                                setActiveLandTool(newMode);
                                            }
                                        }}
                                        size="small"
                                        fullWidth
                                    >
                                        <ToggleButton value="raise" aria-label="raise">
                                            {t('map_creator.brush_panel.raise')}
                                        </ToggleButton>
                                        <ToggleButton value="sink" aria-label="sink">
                                            {t('map_creator.brush_panel.sink')}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                <Box>
                                    <Typography variant="caption" gutterBottom>{t('map_creator.brush_panel.roughness', { roughness })}</Typography>
                                    <Slider
                                        min={0}
                                        max={5}
                                        step={1}
                                        value={roughness}
                                        onChange={(_, v) => setRoughness(sliderValue(v))}
                                        size="small"
                                    />
                                </Box>
                            </Stack>
                        )}

                        {activeLandTool === 'paint' && (
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="caption" gutterBottom>{t('map_creator.brush_panel.opacity', { percent: Math.round(paintOpacity * 100) })}</Typography>
                                    <Slider
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        value={paintOpacity}
                                        onChange={(_, v) => setPaintOpacity(sliderValue(v))}
                                        size="small"
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="caption" display="block" mb={1}>{t('map_creator.brush_panel.terrain_style')}</Typography>
                                    <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={1}>
                                        {terrainBrushes.map(tb => {
                                            const Icon = tb.icon;
                                            const brushColors: Record<string, string> = {
                                                land: '#c9b88a',
                                                mountain: '#5a6575',
                                                forest: '#3b6637',
                                                desert: '#e29c3a',
                                                'farm land': '#8eb358'
                                            };
                                            const color = brushColors[tb.name] || brushColors.land;
                                            const isSelected = brush === tb.name;
                                            return (
                                                <Tooltip key={tb.name} title={t(`map_creator.brush_panel.brushes.${tb.name.replace(' ', '_')}`)}>
                                                    <ToggleButton
                                                        value={tb.name}
                                                        selected={isSelected}
                                                        onChange={() => setBrush(tb.name)}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: color,
                                                            border: isSelected ? '2.5px solid #000000' : '1px solid rgba(0,0,0,0.15)',
                                                            aspectRatio: '1/1',
                                                            '&:hover': {
                                                                bgcolor: color,
                                                                filter: 'brightness(0.9)',
                                                            },
                                                            '&.Mui-selected': {
                                                                bgcolor: color,
                                                                border: '2.5px solid #000000',
                                                                boxShadow: '0 0 8px rgba(0,0,0,0.6)',
                                                                '&:hover': {
                                                                    bgcolor: color,
                                                                    filter: 'brightness(0.9)',
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Icon sx={{ fontSize: '18px', color: '#000000' }} />
                                                    </ToggleButton>
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>
        </Stack>
    );
}
